// frontend/src/components/PatientForm.jsx

import React, { useState } from 'react';
import { User, Mail, Phone, Calendar, ClipboardList, Plus, Minus, X, DollarSign } from 'lucide-react';

// O formulário agora recebe a função 'onCadastroSubmit'
export default function PatientForm({ onCadastroSubmit }) {
  const [form, setForm] = useState({
    nome: '', email: '', telefone: '', dateOfBirth: '', sex: '',
    diagnosisTitle: '', diagnosisDescription: '', numberOfSessions: 1,
    tipoCobranca: 'sessao', valorSessao: '', valorPacote: '', valorRecebido: 0,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: name === 'numberOfSessions' ? parseInt(value, 10) : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onCadastroSubmit) {
      // Chama a função da página pai
      const payload = {
        ...form,
        valorSessao: form.valorSessao !== '' ? parseFloat(form.valorSessao) : null,
        valorPacote: form.valorPacote !== '' ? parseFloat(form.valorPacote) : null,
      };
      const success = await onCadastroSubmit(payload);
      // Se a função retornar sucesso, limpa o formulário
      if (success) {
        setForm({
          nome: '', email: '', telefone: '', dateOfBirth: '', sex: '',
          diagnosisTitle: '', diagnosisDescription: '', numberOfSessions: 1,
          tipoCobranca: 'sessao', valorSessao: '', valorPacote: '', valorRecebido: 0,
        });
      }
    }
  };

  const clearForm = () => {
    setForm({
      nome: '', email: '', telefone: '', dateOfBirth: '', sex: '',
      diagnosisTitle: '', diagnosisDescription: '', numberOfSessions: 1,
    });
  };

  // Incrementar/Decrementar sessões
  const incrementSessions = () => {
    setForm({ ...form, numberOfSessions: form.numberOfSessions + 1 });
  };

  const decrementSessions = () => {
    if (form.numberOfSessions > 1) {
      setForm({ ...form, numberOfSessions: form.numberOfSessions - 1 });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Stepper Visual */}
      <div className="flex items-center justify-between mb-8">
        {/* Etapa 1 */}
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center font-bold">1</div>
          <p className="text-xs font-semibold text-gray-700 mt-2">Dados Pessoais</p>
        </div>

        {/* Linha */}
        <div className="flex-1 h-1 bg-gray-300 mx-4 mt-5"></div>

        {/* Etapa 2 */}
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center font-bold">2</div>
          <p className="text-xs text-gray-500 mt-2">Diagnóstico</p>
        </div>

        {/* Linha */}
        <div className="flex-1 h-1 bg-gray-300 mx-4 mt-5"></div>

        {/* Etapa 3 */}
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 rounded-full bg-gray-300 text-gray-600 flex items-center justify-center font-bold">3</div>
          <p className="text-xs text-gray-500 mt-2">Confirmação</p>
        </div>
      </div>

      {/* Seção Dados Pessoais */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Dados Pessoais</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nome do Paciente */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Paciente <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                name="nome"
                placeholder="Ex: João Silva"
                value={form.nome}
                onChange={handleChange}
                required
                className="pl-10 border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-gray-400">(opcional)</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="email"
                name="email"
                placeholder="Ex: joao@email.com"
                value={form.email}
                onChange={handleChange}
                className="pl-10 border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telefone <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                name="telefone"
                placeholder="(00) 00000-0000"
                value={form.telefone}
                onChange={handleChange}
                required
                className="pl-10 border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Data de Nascimento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data de Nascimento <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="date"
                name="dateOfBirth"
                value={form.dateOfBirth}
                onChange={handleChange}
                required
                className="pl-10 border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Sexo */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sexo <span className="text-red-500">*</span>
            </label>
            <select
              name="sex"
              value={form.sex}
              onChange={handleChange}
              required
              className="border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Selecione o Sexo</option>
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
              <option value="Outro">Outro</option>
            </select>
          </div>
        </div>
      </div>

      {/* Seção Diagnóstico */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <ClipboardList className="text-amber-600" size={24} />
          <h3 className="text-lg font-semibold text-amber-700">Diagnóstico Fisioterapêutico</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Título do Diagnóstico */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título do Diagnóstico <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="diagnosisTitle"
              placeholder="Ex: Lombalgia"
              value={form.diagnosisTitle}
              onChange={handleChange}
              className="border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Descrição - Spanning 2 columns */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição Detalhada
            </label>
            <textarea
              name="diagnosisDescription"
              placeholder="Descrição detalhada do que o paciente está sentindo..."
              value={form.diagnosisDescription}
              onChange={handleChange}
              className="border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="4"
            />
          </div>
        </div>
      </div>

      {/* Seção Número de Sessões */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Número de Sessões <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={decrementSessions}
            className="w-10 h-10 rounded-lg bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800 transition"
          >
            <Minus size={20} />
          </button>
          <input
            type="number"
            name="numberOfSessions"
            value={form.numberOfSessions}
            min={1}
            max={500}
            onChange={(e) => setForm({ ...form, numberOfSessions: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
            onBlur={(e) => {
              const val = parseInt(e.target.value, 10);
              setForm({ ...form, numberOfSessions: isNaN(val) || val < 1 ? 1 : val > 500 ? 500 : val });
            }}
            className="w-20 text-center text-2xl font-semibold text-gray-900 border border-gray-200 rounded-xl py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <button
            type="button"
            onClick={incrementSessions}
            className="w-10 h-10 rounded-lg bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800 transition"
          >
            <Plus size={20} />
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-2">Quantidade inicial prevista de sessões</p>
      </div>

      {/* Seção Financeiro */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <DollarSign className="text-blue-600" size={22} />
          <h3 className="text-lg font-semibold text-blue-700">Cobrança</h3>
        </div>

        {/* Tipo de Cobrança */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-3">Tipo de Cobrança</label>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="tipoCobranca"
                value="sessao"
                checked={form.tipoCobranca === 'sessao'}
                onChange={handleChange}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">Por Sessão</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="tipoCobranca"
                value="pacote"
                checked={form.tipoCobranca === 'pacote'}
                onChange={handleChange}
                className="accent-blue-600"
              />
              <span className="text-sm text-gray-700">Pacote Fechado</span>
            </label>
          </div>
        </div>

        {form.tipoCobranca === 'sessao' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valor da Sessão (R$)</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                type="number"
                name="valorSessao"
                placeholder="0,00"
                value={form.valorSessao}
                min={0}
                step="0.01"
                onChange={handleChange}
                className="pl-9 border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
            {form.valorSessao && form.numberOfSessions && (
              <p className="text-sm text-blue-700 font-medium mt-2">
                Receita prevista: R$ {(parseFloat(form.valorSessao) * form.numberOfSessions).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valor do Pacote (R$)</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                type="number"
                name="valorPacote"
                placeholder="0,00"
                value={form.valorPacote}
                min={0}
                step="0.01"
                onChange={handleChange}
                className="pl-9 border border-gray-300 rounded-lg p-3 w-full focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* Rodapé com Botões */}
      <div className="flex gap-3 justify-end pt-6 border-t">
        <button
          type="button"
          onClick={clearForm}
          className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition"
        >
          <X size={20} />
          Limpar formulário
        </button>
        <button
          type="submit"
          className="flex items-center gap-2 px-6 py-3 bg-blue-700 text-white rounded-lg font-medium hover:bg-blue-800 transition"
        >
          <Plus size={20} />
          Cadastrar Paciente
        </button>
      </div>
    </form>
  );
}